/**
 * CL-INVOICE-001C2B — Selective invoicing permission editor (pure).
 *
 * Plain-language invoice controls map to granular staff financial keys.
 * Dependencies are added on enable and pruned on disable without breaking
 * unrelated collaborator or staff financial permissions.
 */

import { projectAccountingPermissionsUiEnabled } from "./projectAccountingPermissionsFeatureGate";
import {
  LEGACY_INVOICE_PERMISSION_KEY,
  STAFF_FINANCIAL_MANAGE_PERMISSION_KEYS,
  STAFF_FINANCIAL_VIEW_PERMISSION_KEYS,
} from "./projectStaffFinancialPermissionsModel";

export const FULL_INVOICE_ACCESS_CONTROL_KEY = "fullInvoiceAccess";

/** Project-scoped invoice workflow — excludes company-wide numbering/settings. */
export const PROJECT_INVOICE_CAPABILITY_KEYS = Object.freeze([
  "canViewInvoices",
  "canPrepareInvoices",
  "canReviewInvoices",
  "canApproveInvoices",
  "canShareClientTest",
  "canIssueInvoices",
]);

/** Parent keys that may be persisted when required by the invoice dependency model. */
export const PERSISTED_INVOICE_DEPENDENCY_PARENT_KEYS = Object.freeze([
  "canAccessFinancials",
  "canAccessTimeLog",
]);

export const PERSISTED_INVOICE_PERMISSION_KEYS = Object.freeze([
  ...PROJECT_INVOICE_CAPABILITY_KEYS,
  ...PERSISTED_INVOICE_DEPENDENCY_PARENT_KEYS,
]);

export function pickPersistedInvoicePermissionFlags(raw = {}) {
  return PERSISTED_INVOICE_PERMISSION_KEYS.reduce((acc, key) => {
    if (typeof raw?.[key] === "boolean") acc[key] = raw[key];
    return acc;
  }, {});
}

export const COMPANY_INVOICE_SETTINGS_PERMISSION_KEY = "canManageInvoiceSettings";

export const INVOICE_PERMISSION_CONTROL_KEYS = Object.freeze([
  ...PROJECT_INVOICE_CAPABILITY_KEYS,
  COMPANY_INVOICE_SETTINGS_PERMISSION_KEY,
]);

export const INVOICE_PERMISSION_CONTROL_LABELS = Object.freeze({
  canViewInvoices: "View invoices",
  canPrepareInvoices: "Prepare and edit invoices",
  canReviewInvoices: "Review invoices and return for changes",
  canApproveInvoices: "Approve invoices internally",
  canShareClientTest: "Send client-test copies",
  canIssueInvoices: "Issue and send final invoices",
  canManageInvoiceSettings: "Manage company invoice settings and numbering (Admin only)",
  [FULL_INVOICE_ACCESS_CONTROL_KEY]: "Full invoice access",
});

const INVOICE_CONTROL_DEPENDENTS = Object.freeze({
  canViewInvoices: [
    "canPrepareInvoices",
    "canReviewInvoices",
    "canApproveInvoices",
    "canShareClientTest",
    "canIssueInvoices",
  ],
  canPrepareInvoices: ["canManageInvoiceSettings"],
  canReviewInvoices: ["canApproveInvoices", "canShareClientTest", "canIssueInvoices"],
  canApproveInvoices: ["canIssueInvoices"],
});

const INVOICE_CHILD_PARENTS = Object.freeze([
  { child: "canViewInvoices", parents: ["canAccessFinancials"] },
  {
    child: "canPrepareInvoices",
    parents: ["canAccessFinancials", "canViewInvoices", "canAccessTimeLog", "canViewTimeLog"],
  },
  { child: "canReviewInvoices", parents: ["canAccessFinancials", "canViewInvoices"] },
  { child: "canApproveInvoices", parents: ["canReviewInvoices"] },
  { child: "canShareClientTest", parents: ["canReviewInvoices"] },
  { child: "canIssueInvoices", parents: ["canApproveInvoices"] },
  { child: "canManageInvoiceSettings", parents: ["canPrepareInvoices"] },
]);

const INVOICE_MANAGED_PARENT_KEYS = Object.freeze([
  "canAccessFinancials",
  "canAccessTimeLog",
  "canViewTimeLog",
  "canViewInvoices",
  "canPrepareInvoices",
  "canReviewInvoices",
  "canApproveInvoices",
  "canShareClientTest",
  "canIssueInvoices",
  "canManageInvoiceSettings",
]);

const NON_INVOICE_FINANCIAL_PARENT_REASONS = Object.freeze([
  { key: "canAccessFinancials", requiresAny: ["canViewBudget", "canManageBudget", "canViewReceipts", "canManageReceipts", "canViewPayments", "canRecordPayments"] },
  { key: "canAccessTimeLog", requiresAny: ["canManageTimeEntries"] },
  { key: "canViewTimeLog", requiresAny: ["canLogTime", "canManageTimeEntries"] },
]);

function clonePermissions(permissions = {}) {
  return { ...(permissions || {}) };
}

function invoiceControlEnabled(permissions = {}, key = "") {
  return permissions?.[key] === true;
}

export function hasExplicitInvoicePermissionKeys(permissions = {}) {
  return [
    ...INVOICE_PERMISSION_CONTROL_KEYS,
    "canAccessFinancials",
    "canAccessTimeLog",
  ].some((key) => typeof permissions?.[key] === "boolean");
}

export function assignmentUsesLegacyInvoiceFallback(permissions = {}) {
  return (
    permissions?.[LEGACY_INVOICE_PERMISSION_KEY] === true &&
    !hasExplicitInvoicePermissionKeys(permissions)
  );
}

export function isFullInvoiceAccessEnabled(permissions = {}) {
  return PROJECT_INVOICE_CAPABILITY_KEYS.every((key) => invoiceControlEnabled(permissions, key));
}

function parentsRequiredByEnabledInvoiceControls(permissions = {}) {
  const required = new Set();
  INVOICE_CHILD_PARENTS.forEach(({ child, parents }) => {
    if (!invoiceControlEnabled(permissions, child)) return;
    required.add(child);
    parents.forEach((parent) => required.add(parent));
  });
  if (invoiceControlEnabled(permissions, "canApproveInvoices")) required.add("canReviewInvoices");
  if (invoiceControlEnabled(permissions, "canIssueInvoices")) {
    required.add("canApproveInvoices");
    required.add("canReviewInvoices");
  }
  return required;
}

function nonInvoiceReasonToKeepParent(permissions = {}, parentKey = "") {
  const rule = NON_INVOICE_FINANCIAL_PARENT_REASONS.find((row) => row.key === parentKey);
  if (!rule) return false;
  return rule.requiresAny.some((key) => invoiceControlEnabled(permissions, key));
}

function staffFinancialReasonToKeepParent(permissions = {}, parentKey = "") {
  if (parentKey === "canAccessFinancials") {
    return STAFF_FINANCIAL_VIEW_PERMISSION_KEYS.filter((key) => key !== "canViewInvoices").some((key) =>
      invoiceControlEnabled(permissions, key)
    ) || STAFF_FINANCIAL_MANAGE_PERMISSION_KEYS.some((key) => invoiceControlEnabled(permissions, key));
  }
  return false;
}

export function normalizeInvoicePermissionDependencies(permissions = {}) {
  const next = clonePermissions(permissions);
  if (invoiceControlEnabled(next, "canIssueInvoices")) {
    next.canApproveInvoices = true;
    next.canReviewInvoices = true;
  }
  if (invoiceControlEnabled(next, "canApproveInvoices")) {
    next.canReviewInvoices = true;
  }
  let changed = true;
  while (changed) {
    changed = false;
    INVOICE_CHILD_PARENTS.forEach(({ child, parents }) => {
      if (!invoiceControlEnabled(next, child)) return;
      next[child] = true;
      parents.forEach((parent) => {
        if (next[parent] !== true) {
          next[parent] = true;
          changed = true;
        }
      });
    });
  }
  return next;
}

export function pruneInvoicePermissionDependencies(permissions = {}) {
  const next = clonePermissions(permissions);
  const required = parentsRequiredByEnabledInvoiceControls(next);

  INVOICE_MANAGED_PARENT_KEYS.forEach((key) => {
    if (INVOICE_PERMISSION_CONTROL_KEYS.includes(key)) {
      if (!required.has(key) && typeof next[key] === "boolean") {
        next[key] = false;
      }
      return;
    }
    if (required.has(key)) {
      next[key] = true;
      return;
    }
    if (nonInvoiceReasonToKeepParent(next, key) || staffFinancialReasonToKeepParent(next, key)) {
      return;
    }
    if (typeof next[key] === "boolean") {
      next[key] = false;
    }
  });

  return next;
}

function disableInvoiceControlDependents(permissions = {}, key = "") {
  const next = clonePermissions(permissions);
  const queue = [...(INVOICE_CONTROL_DEPENDENTS[key] || [])];
  next[key] = false;
  while (queue.length) {
    const dependent = queue.shift();
    if (next[dependent] !== true) continue;
    next[dependent] = false;
    queue.push(...(INVOICE_CONTROL_DEPENDENTS[dependent] || []));
  }
  return next;
}

export function applyInvoicePermissionToggle(permissions = {}, key = "", checked = false) {
  if (key === FULL_INVOICE_ACCESS_CONTROL_KEY) {
    const next = clonePermissions(permissions);
    PROJECT_INVOICE_CAPABILITY_KEYS.forEach((controlKey) => {
      next[controlKey] = checked === true;
    });
    return checked ? normalizeInvoicePermissionDependencies(next) : pruneInvoicePermissionDependencies(next);
  }

  if (checked === true) {
    return normalizeInvoicePermissionDependencies({
      ...permissions,
      [key]: true,
    });
  }

  const withoutDependents = disableInvoiceControlDependents(permissions, key);
  return pruneInvoicePermissionDependencies(withoutDependents);
}

export function mergeInvoicePermissionsPreservingUnrelated(existing = {}, patch = {}) {
  return {
    ...existing,
    ...patch,
  };
}

export function normalizeInvoicePermissionsForAssignmentSave(
  permissions = {},
  { accountingPermissionsUiEnabled = null, rawPermissions = null } = {}
) {
  if (!projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return { ...permissions };
  }

  const source = rawPermissions || permissions;
  let next = {
    ...clonePermissions(permissions),
    ...pickPersistedInvoicePermissionFlags(source),
  };

  if (hasExplicitInvoicePermissionKeys(source)) {
    next = normalizeInvoicePermissionDependencies(next);
    next = pruneInvoicePermissionDependencies(next);
    delete next[LEGACY_INVOICE_PERMISSION_KEY];
  }

  return next;
}

export function listProjectInvoicePermissionControls() {
  return PROJECT_INVOICE_CAPABILITY_KEYS.map((key) => ({
    key,
    label: INVOICE_PERMISSION_CONTROL_LABELS[key],
  }));
}

export function listInvoicePermissionControls() {
  return INVOICE_PERMISSION_CONTROL_KEYS.map((key) => ({
    key,
    label: INVOICE_PERMISSION_CONTROL_LABELS[key],
  }));
}
